import { Link } from '@tanstack/react-router'
import { useState } from 'react'
import { Library, Loader2, X } from 'lucide-react'
import {
  estimateTokensFromChars,
  type SavedPromptRead,
} from '@/api/docfiller-types'
import { useSavedPrompts } from '@/api/queries'
import { useCurrentWorkspace } from '@/api/workspace-context-provider'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'

interface Props {
  selected: SavedPromptRead | null
  onSelect: (prompt: SavedPromptRead | null) => void
  /** Disabled while a run is in flight. */
  disabled?: boolean
}

/**
 * Compact dropdown for selecting a saved prompt to layer into the next fill
 * run. Shows the title + an estimate of how many tokens the prompt adds so
 * the user can avoid blowing out the budget. The selected prompt renders
 * as a removable chip next to the trigger.
 *
 * Empty-library path links the user to ``/prompts`` so the action stays
 * one click away even on a brand-new account.
 */
export function SavedPromptPicker({ selected, onSelect, disabled }: Props) {
  const { currentWorkspaceUuid } = useCurrentWorkspace()
  const { data: prompts, isLoading, error } = useSavedPrompts(currentWorkspaceUuid)
  const [open, setOpen] = useState(false)

  return (
    <div className="inline-flex items-center gap-1.5" data-testid="saved-prompt-picker">
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={disabled}
            data-testid="saved-prompt-trigger"
            aria-label="Insert a saved prompt"
          >
            <Library className="size-3.5" aria-hidden />
            {selected ? 'Saved prompt' : 'Use saved prompt'}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-72"
          data-testid="saved-prompt-menu"
        >
          <DropdownMenuLabel>Workspace prompt library</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {isLoading && (
            <div className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground">
              <Loader2 className="size-3 animate-spin" aria-hidden />
              Loading prompts…
            </div>
          )}
          {error && (
            <div
              role="alert"
              className="px-2 py-2 text-xs text-destructive"
              data-testid="saved-prompt-error"
            >
              {error instanceof Error ? error.message : 'Failed to load prompts'}
            </div>
          )}
          {!isLoading && !error && (prompts?.length ?? 0) === 0 && (
            <div className="px-2 py-2 text-xs text-muted-foreground">
              <p>No saved prompts yet.</p>
              <Link
                to="/prompts"
                className="mt-1 inline-flex text-primary underline-offset-2 hover:underline"
                data-testid="saved-prompt-go-library"
              >
                Open prompt library →
              </Link>
            </div>
          )}
          {prompts?.map((prompt) => {
            const active = selected?.uuid === prompt.uuid
            return (
              <DropdownMenuItem
                key={prompt.uuid}
                data-testid={`saved-prompt-option-${prompt.uuid}`}
                className={cn(
                  'flex flex-col items-start gap-0.5',
                  active && 'bg-accent text-accent-foreground',
                )}
                onSelect={() => {
                  onSelect(active ? null : prompt)
                  setOpen(false)
                }}
              >
                <span className="text-xs font-medium">{prompt.name}</span>
                <span className="text-[10px] text-muted-foreground">
                  ~{estimateTokensFromChars(prompt.content.length).toLocaleString()} tokens
                </span>
              </DropdownMenuItem>
            )
          })}
          {selected && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="saved-prompt-clear"
                onSelect={() => {
                  onSelect(null)
                  setOpen(false)
                }}
              >
                <X className="size-3.5" aria-hidden />
                Clear selection
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {selected && (
        <Badge
          tone="primary"
          size="xs"
          data-testid="saved-prompt-chip"
          className="max-w-[12rem] truncate"
        >
          {selected.name}
        </Badge>
      )}
    </div>
  )
}
